import envConfig from './env-config';
import kenticoConfig from '../kentico-config.json';
import * as models from '../kontent-models';
import { DeliveryClient } from '@kontent-ai/delivery-sdk';
import { enums } from '.';
import { getKontent } from './remote-config-manager';

/*******************************************************************************
 * cms-client.js creates / exports an instance of the Kentico DeliveryClient
 * to use throughout the application to interact with the Kentico Delivery API
 ******************************************************************************/
let cmsClient: DeliveryClient;
let erClient: DeliveryClient;
let tasksClient: DeliveryClient;
let referenceCmsClient: DeliveryClient;

function getReferenceCmsClient() {
  if (!referenceCmsClient) {
    referenceCmsClient = new DeliveryClient({
      environmentId: kenticoConfig.referenceProjectId,
      proxy: {
        baseUrl: `${window.location.origin}/kontent`,
        basePreviewUrl: `${window.location.origin}/kontent-preview`,
      },
    });
  }
  return referenceCmsClient;
}

export const getReferenceValues = async () => {
  const envCodename = envConfig.upEnvironment?.toLowerCase().replace('.', '_');
  let result;
  try {
    result = await getKontent<models.SimpleRemoteConfigurationItem>({
      cmsClient: getReferenceCmsClient(),
      kontentType: 'simple_remote_configuration_item',
      cacheKey: 'reference_remote_config',
      kontentElementsParameters: [envCodename],
    });
  } catch {
    return {};
  }

  const refItems: { [name: string]: any } = {};

  for (const item of result.items) {
    const codename = item.system.codename;
    const elements = item.elements as { [key: string]: { value: any } };
    refItems[codename] = elements[envCodename].value;
  }

  return refItems;
};

const getClient = async (): Promise<DeliveryClient> => {
  if (cmsClient) {
    return cmsClient;
  }

  const refItems = await getReferenceValues();

  try {
    cmsClient = new DeliveryClient({
      environmentId: refItems[enums.CodeNames.refProjectId],
      proxy: {
        baseUrl: `${window.location.origin}/kontent`,
        basePreviewUrl: `${window.location.origin}/kontent-preview`,
      },
      previewApiKey: refItems[enums.CodeNames.refPreviewKey],
      defaultQueryConfig: {
        usePreviewMode:
          !envConfig.isProd &&
          refItems[enums.CodeNames.refPreviewFlag] === 'true',
      },
    });
  } catch {
    // DeliveryClient construction can fail in test environments (e.g. Parse5
    // unavailability in Jest with coverage). Return a chainable stub.
    const chainableStub: any = new Proxy(
      {},
      {
        get: (_target, prop) => {
          if (prop === 'toPromise' || prop === 'toObservable') {
            return () => Promise.resolve({ items: [], pagination: {} });
          }
          return (..._args: any[]) => chainableStub;
        },
      },
    );
    return chainableStub;
  }

  return cmsClient;
};

const employerContentClient = () => {
  if (erClient) {
    return erClient;
  }

  erClient = new DeliveryClient({
    environmentId: kenticoConfig.erProjectId,
    proxy: {
      baseUrl: `${window.location.origin}/kontent`,
      basePreviewUrl: `${window.location.origin}/kontent-preview`,
    },
    previewApiKey: kenticoConfig.previewERProjectId,
    defaultQueryConfig: {
      usePreviewMode: !envConfig.isProd,
    },
  });

  return erClient;
};

const getTasksClient = async () => {
  if (tasksClient) {
    return tasksClient;
  }

  const refItems = await getReferenceValues();

  tasksClient = new DeliveryClient({
    environmentId: refItems[enums.CodeNames.refTasksProjectId],
    proxy: {
      baseUrl: `${window.location.origin}/kontent`,
      basePreviewUrl: `${window.location.origin}/kontent-preview`,
    },
    previewApiKey: refItems[enums.CodeNames.refTasksPreviewKey],
    defaultQueryConfig: {
      usePreviewMode:
        !envConfig.isProd &&
        refItems[enums.CodeNames.refTasksPreviewFlag] === 'true',
    },
  });

  return tasksClient;
};
/*******************************************************************************
 * exported api definition
 ******************************************************************************/
export { getClient, employerContentClient, getTasksClient, models };
